// Миры и их фоны — из assets-game/raw/world-NN/bg-*.png (все готовы для всех миров).
// Выбор мира в конструкторе ограничивает выбор фона фонами этого мира.
// Ключ фона = descriptive-суффикс имени файла (bg-NN-<key>.png). Плеер медиа резолвит
// мульти-мирово (assets.ts createMedia): фон и тайлы террейна — из папки мира уровня
// (bgUrl/world из данных уровня); в world-01 пока живут только общие спрайты
// (житель/портал/тулы — арт прочих миров не сгенерирован).
//
// Модуль общий для конструктора и боевого хоста (useLevelHost): фон боевого уровня строится
// ТОЙ ЖЕ функцией, что и в «Запустить» конструктора, — что автор видел, то и уедет в бой.
// Раньше бой брал фон из легаси-папки плеера, где у мира 1 лежат только три первых фона,
// и уровни на chasm/golden-hour/crystals молча показывали луг по номеру уровня.

export interface WorldDef { id: string; label: string; backgrounds: string[] }

export const WORLDS: WorldDef[] = [
  { id: 'world-01', label: 'Мир 1 · Прыжки', backgrounds: ['meadow', 'foothills', 'sky', 'chasm', 'golden-hour', 'crystals'] },
  { id: 'world-02', label: 'Мир 2 · Тир', backgrounds: ['range-yard', 'furnace-glow', 'lamp-row', 'target-butts', 'bulkhead-wall', 'blind-sentry'] },
  { id: 'world-03', label: 'Мир 3 · Гонки', backgrounds: ['sunset-horizon', 'banked-curve', 'grandstand-row', 'finish-gate', 'palm-dusk', 'jitter-break'] },
  { id: 'world-04', label: 'Мир 4 · Тетрис', backgrounds: ['tetromino-skyline', 'deep-well', 'grid-dusk', 'violet-storm', 'frozen-rows', 'boss-tilt'] },
  { id: 'world-05', label: 'Мир 5 · Осада', backgrounds: ['parchment-hills', 'forest-front', 'river-crossing', 'mountain-pass', 'gold-vein', 'scorched-flank'] },
  { id: 'world-06', label: 'Мир 6 · Стелс', backgrounds: ['night-yard', 'double-cones', 'vent-maze', 'laser-fence', 'vault-glow', 'boss-arena'] },
  { id: 'world-07', label: 'Мир 7 · Руны', backgrounds: ['vault-corridor', 'torch-hall', 'rune-cracks', 'treasure-vault', 'deep-crypt', 'boss-lair'] },
  { id: 'world-08', label: 'Мир 8 · Стройка', backgrounds: ['materializing-room', 'lowpoly-vale', 'wireframe-scaffold', 'taped-seams', 'white-noise', 'boss-static'] },
  { id: 'world-09', label: 'Мир 9 · Город', backgrounds: ['town-skyline', 'canal-bridges', 'green-park', 'harbor-quarter', 'target-quarter', 'boss-collapse'] },
  { id: 'world-10', label: 'Мир 10 · Космос', backgrounds: ['void-nebula', 'station-dock', 'docking-ports', 'gimbal-rings', 'planet-approach', 'boss-lockvortex'] },
  { id: 'world-11', label: 'Мир 11 · Нетворкинг', backgrounds: ['arena-overview', 'team-bases', 'ping-tracers', 'snapshot-ghosts', 'server-core', 'boss-desync'] },
  { id: 'world-12', label: 'Мир 12 · Железо', backgrounds: ['motherboard-floor', 'ember-buses', 'world-slots', 'root-shaft', 'drift-grey', 'boss-lair'] },
];

export function worldById(id: string): WorldDef | undefined {
  return WORLDS.find((w) => w.id === id);
}

/** Фоны выбранного мира; неизвестный мир → фоны world-01. */
export function backgroundsOf(worldId: string): string[] {
  return (worldById(worldId) ?? WORLDS[0]).backgrounds;
}

/** Фон, допустимый в этом мире: свой остаётся, чужой заменяется первым фоном мира.
 *  ОДНО место на весь конструктор: тем же клампом живут смена мира в топбаре
 *  (ConstructorScreen) и предпросмотр публикации (publishPlan). Разъедься эти две
 *  копии — диалог обещал бы один фон, а сохранённый файл получал другой. */
export function clampBg(worldId: string, bg: string): string {
  const bgs = backgroundsOf(worldId);
  return bgs.includes(bg) ? bg : bgs[0];
}

/** Имя файла фона на S3: bg-NN-<key>.png (NN = позиция в списке мира, с 01). */
export function bgFileName(worldId: string, bgKey: string): string | null {
  const idx = backgroundsOf(worldId).indexOf(bgKey);
  if (idx < 0) return null;
  return `bg-${String(idx + 1).padStart(2, '0')}-${bgKey}.png`;
}

/** Публичный URL фона: {mediaBaseUrl}/backgrounds/world-NN/bg-NN-<key>.png (залито tools/media). */
export function bgUrl(mediaBaseUrl: string, worldId: string, bgKey: string): string | null {
  const file = bgFileName(worldId, bgKey);
  if (!file) return null;
  return `${mediaBaseUrl.replace(/\/+$/, '')}/backgrounds/${worldId}/${file}`;
}

/** Данные уровня для плеера с готовым `bgUrl`. Плеер сам медиа-корень не знает (у него только
 *  player-base), поэтому адрес фона строит хост. Уже заданный автором `bgUrl` не трогаем;
 *  неизвестный миру ключ — данные как есть (плеер упадёт в свой легаси-фолбэк). */
export function withBgUrl(data: unknown, mediaBaseUrl: string | undefined): unknown {
  if (!mediaBaseUrl || !data || typeof data !== 'object') return data;
  const d = data as { bgUrl?: unknown; world?: unknown; bg?: unknown };
  if (typeof d.bgUrl === 'string' && d.bgUrl) return data;
  if (typeof d.bg !== 'string') return data;
  const url = bgUrl(mediaBaseUrl, typeof d.world === 'string' ? d.world : 'world-01', d.bg);
  return url ? { ...d, bgUrl: url } : data;
}
